import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import Avatar, { getMentor } from "../components/avatars/AvatarLibrary";
import { Button } from "../components/ui/button";
import { Send, Volume2, VolumeX, ArrowRight, Mic, Square, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/analytics";
import { celebrate } from "../lib/celebrate";
import TrustLine from "../components/TrustLine";
import { resolveFirstName } from "../lib/firstName";

/**
 * Branching onboarding:
 *   Q0 — pick path: foundation (standard 7-Q) or elite (Titan 4-Q).
 *   Standard path: the 7-question flow below.
 *   Elite path: 4 Titan-tier questions, routes to /pricing with "elite" tier highlighted.
 */

const Q0_PATH = [
  { label: "A Mentor for my foundation", sub: "Calm, steady, small wins — I'm rebuilding or starting over", value: "foundation" },
  { label: "A Strategist for my legacy", sub: "Direct, data-dense — CEO / founder / operator scaling", value: "elite" },
];

const ELITE_Q1 = [
  { label: "Scaling revenue", value: "scaling_revenue" },
  { label: "Building legacy", value: "building_legacy" },
  { label: "Personal optimization", value: "personal_optimization" },
  { label: "Strategic exit / Next phase", value: "strategic_exit" },
];
const ELITE_Q2 = [
  { label: "Time fragmentation", value: "time_fragmentation" },
  { label: "Strategic blindspots", value: "strategic_blindspots" },
  { label: "Decision fatigue", value: "decision_fatigue" },
  { label: "Executive isolation", value: "executive_isolation" },
];
const ELITE_Q3 = [
  { label: "Deep-dive market intelligence", value: "market_intel" },
  { label: "Psychological resilience", value: "resilience" },
  { label: "High-performance habit tuning", value: "habit_tuning" },
  { label: "Global case-studies", value: "case_studies" },
];
const ELITE_Q4 = [
  { label: "Data-dense reports", value: "data_dense" },
  { label: "Direct strategic advice", value: "direct_advice" },
  { label: "Habit-tracking with metrics", value: "metrics" },
  { label: "24/7 concierge response", value: "concierge" },
];

// Standard-path steps: Q0 (path) + the 7 foundation questions.
const STANDARD_STEPS = (ctx) => [
  {
    id: "path",
    bubbles: [
      `Welcome to Ascend, ${ctx.firstName || "friend"}.`,
      "Before we start — which path best describes you?",
    ],
    chips: Q0_PATH,
    save: (v) => ({ path: v }),
    isPath: true,
  },
  {
    id: "life",
    bubbles: [
      "I want to understand what's really going on. Let's start simple.",
      "How are you feeling about your life right now?",
    ],
    chips: Q1_LIFE,
    save: (v) => ({ life_feeling: v }),
  },
  {
    id: "focus",
    bubbles: [
      "Heard. That gives me a real sense of where you are.",
      "What should we focus on together — starting today?",
    ],
    chips: Q2_FOCUS,
    save: (v) => ({ focus_area: v, _focus_pillar: Q2_FOCUS.find((c) => c.value === v)?.pillar || "endgame" }),
  },
  {
    id: "branch",
    bubbles: (a) => {
      const labelMap = {
        career: "career", relationships: "relationships", health: "health",
        finances: "finances", purpose: "sense of purpose", confidence: "confidence",
      };
      const l = labelMap[a.focus_area] || "that";
      return [`Okay — ${l}. Let's sharpen it.`, "What matters most to you here?"];
    },
    chips: (a) => Q3_BRANCH[a.focus_area] || Q3_BRANCH.career,
    typeable: true,
    placeholder: "Or describe it in your words…",
    save: (v) => ({ focus_subgoal: v }),
  },
  {
    id: "energy",
    bubbles: ["Good. I'll hold that.", "Now tell me — what energizes you most?"],
    chips: Q4_ENERGIZES,
    save: (v) => ({ energy_source: v }),
  },
  {
    id: "blocker",
    bubbles: ["Thanks for being honest with me.", "What usually gets in your way?"],
    chips: Q5_BLOCKERS,
    save: (v) => ({ blocker: v }),
  },
  {
    id: "support",
    bubbles: [
      "Got it. Knowing that helps me know when to push and when to hold back.",
      "How do you like to be supported?",
    ],
    chips: Q6_SUPPORT,
    save: (v) => ({ support_style: v, coach_style: v === "tools" || v === "explore" ? "calm" : v }),
  },
  {
    id: "followthrough",
    bubbles: ["Last one — and it's the important one.", "What actually helps you follow through?"],
    chips: Q7_FOLLOWTHROUGH,
    save: (v) => ({ followthrough: v }),
  },
];

// Elite-path steps: Q0 (path) + 4 Titan questions.
const ELITE_STEPS = (ctx) => [
  {
    id: "path",
    bubbles: [
      `Welcome to Ascend, ${ctx.firstName || "friend"}.`,
      "Before we start — which path best describes you?",
    ],
    chips: Q0_PATH,
    save: (v) => ({ path: v }),
    isPath: true,
  },
  {
    id: "mission",
    bubbles: [
      "Good. Let's skip the basics.",
      "What is your current mission?",
    ],
    chips: ELITE_Q1,
    save: (v) => ({ elite_mission: v }),
  },
  {
    id: "bottleneck",
    bubbles: ["Noted.", "What's the biggest bottleneck in your current stack?"],
    chips: ELITE_Q2,
    save: (v) => ({ elite_bottleneck: v }),
  },
  {
    id: "edge",
    bubbles: ["Understood.", "What's the edge you're looking for?"],
    chips: ELITE_Q3,
    save: (v) => ({ elite_edge: v }),
  },
  {
    id: "intel_style",
    bubbles: ["Last one.", "How do you prefer to receive intelligence?"],
    chips: ELITE_Q4,
    save: (v) => ({
      elite_intel_style: v,
      coach_style: "nobs", // Titan persona = direct, no-fluff
    }),
  },
];

// Master STEPS: picks the right path based on answers.path.
const STEPS = (ctx) => {
  if (ctx.path === "elite") return ELITE_STEPS(ctx);
  return STANDARD_STEPS(ctx);
};

const Q1_LIFE = [
  { label: "Thriving & optimizing", value: "thriving" },
  { label: "Satisfied but stuck", value: "stuck" },
  { label: "Ready for change", value: "ready" },
  { label: "Overwhelmed", value: "overwhelmed" },
  { label: "Uncertain", value: "uncertain" },
];

const Q2_FOCUS = [
  { label: "Career / Growth", value: "career", pillar: "endgame" },
  { label: "Relationships", value: "relationships", pillar: "relationships" },
  { label: "Health / Wellness", value: "health", pillar: "health" },
  { label: "Finances", value: "finances", pillar: "financial" },
  { label: "Purpose", value: "purpose", pillar: "spirituality" },
  { label: "Confidence", value: "confidence", pillar: "endgame" },
];

const Q3_BRANCH = {
  career: [
    { label: "Fulfilling work", value: "fulfilling_work" },
    { label: "Advancement", value: "advancement" },
    { label: "Confidence at work", value: "work_confidence" },
    { label: "Work–life balance", value: "balance" },
    { label: "Direction", value: "direction" },
    { label: "Stability", value: "stability" },
  ],
  relationships: [
    { label: "Deeper intimacy", value: "intimacy" },
    { label: "Better communication", value: "communication" },
    { label: "Finding the right partner", value: "finding_partner" },
    { label: "Healing old wounds", value: "healing" },
    { label: "Setting boundaries", value: "boundaries" },
    { label: "Showing up for family", value: "family" },
  ],
  health: [
    { label: "More energy", value: "energy" },
    { label: "Losing weight", value: "weight" },
    { label: "Getting stronger", value: "strength" },
    { label: "Better sleep", value: "sleep" },
    { label: "Sober / cutting back", value: "sobriety" },
    { label: "Mental calm", value: "mental_calm" },
  ],
  finances: [
    { label: "Out of debt", value: "debt" },
    { label: "Saving my first $10k", value: "savings" },
    { label: "Earning more", value: "income" },
    { label: "Investing smart", value: "investing" },
    { label: "Budget I can keep", value: "budget" },
    { label: "Financial freedom", value: "freedom" },
  ],
  purpose: [
    { label: "Finding my 'why'", value: "why" },
    { label: "Spiritual practice", value: "practice" },
    { label: "Leaving a legacy", value: "legacy" },
    { label: "Giving back", value: "service" },
    { label: "Deeper faith", value: "faith" },
    { label: "Quiet mind", value: "quiet" },
  ],
  confidence: [
    { label: "Speaking up", value: "speaking_up" },
    { label: "Self-worth", value: "self_worth" },
    { label: "Body image", value: "body_image" },
    { label: "Handling failure", value: "failure" },
    { label: "Social ease", value: "social" },
    { label: "Boldness", value: "boldness" },
  ],
};

const Q4_ENERGIZES = [
  { label: "Achieving clear goals", value: "goals" },
  { label: "Knowing people believe in me", value: "belief" },
  { label: "Making a real impact", value: "impact" },
  { label: "Understanding my 'Why'", value: "why" },
  { label: "Solving practical challenges", value: "challenges" },
];

const Q5_BLOCKERS = [
  { label: "Motivation fades", value: "motivation" },
  { label: "Perfectionism", value: "perfectionism" },
  { label: "Competing priorities", value: "priorities" },
  { label: "Don't know where to start", value: "start" },
  { label: "External circumstances", value: "circumstances" },
];

const Q6_SUPPORT = [
  { label: "Direct & honest", value: "hard" },
  { label: "Gentle validation", value: "calm" },
  { label: "Step-by-step tools", value: "tools" },
  { label: "Deeper exploration", value: "explore" },
  { label: "Regular check-ins", value: "friend" },
];

const Q7_FOLLOWTHROUGH = [
  { label: "Visual progress metrics", value: "metrics" },
  { label: "Supportive community", value: "community" },
  { label: "Personal reflection", value: "reflection" },
  { label: "External accountability", value: "accountability" },
  { label: "Automated habits", value: "automation" },
];

// (legacy inline STEPS removed — now defined above with branching logic)

function buildOnboardingPayload(answers, mentorId) {
  const isElite = answers.path === "elite";
  const pillar = answers._focus_pillar || "endgame";
  const pillars = {
    financial: {},
    health: {},
    spirituality: {},
    relationships: {},
    endgame: {},
  };

  if (isElite) {
    // Elite path: Titan persona, direct no-fluff coach, all fields prefixed elite_
    pillars.endgame.vision = `Elite mission: ${answers.elite_mission || "scaling"}`;
    pillars.endgame.current_state = `Bottleneck: ${answers.elite_bottleneck || ""}`;
    pillars.endgame.priority_goal = `Edge sought: ${answers.elite_edge || ""}`;
  } else {
    pillars[pillar].current_state = `Feeling ${answers.life_feeling || ""}`.trim();
    pillars[pillar].priority_goal = answers.focus_subgoal || "";
    pillars.endgame.vision = answers.focus_subgoal || "";
  }

  return {
    ...pillars,
    coach_style: answers.coach_style || (isElite ? "nobs" : "calm"),
    mentor_id: mentorId,
    path: answers.path || "foundation",
    tier: isElite ? "elite" : "standard",
    // Standard fields
    life_feeling: answers.life_feeling || "",
    focus_area: answers.focus_area || "",
    focus_subgoal: answers.focus_subgoal || "",
    energy_source: answers.energy_source || "",
    blocker: answers.blocker || "",
    support_style: answers.support_style || "",
    followthrough: answers.followthrough || "",
    // Elite fields
    elite_mission: answers.elite_mission || "",
    elite_bottleneck: answers.elite_bottleneck || "",
    elite_edge: answers.elite_edge || "",
    elite_intel_style: answers.elite_intel_style || "",
    completed: true,
  };
}

export default function ConversationalOnboarding() {
  const { user, onboarding, checkAuth } = useAuth();
  const navigate = useNavigate();
  const mentorId = onboarding?.mentor_id;

  const [stepIdx, setStepIdx] = useState(0);
  const [messages, setMessages] = useState([]);
  const [revealedBubbles, setRevealedBubbles] = useState(0);
  const [isMentorTyping, setIsMentorTyping] = useState(false);
  const [answers, setAnswers] = useState({ firstName: resolveFirstName(user, "") });
  const [typeMode, setTypeMode] = useState(false);
  const [textValue, setTextValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [coachSpeaking, setCoachSpeaking] = useState(false);
  const [recordingAnswer, setRecordingAnswer] = useState(false);
  const [voiceProcessing, setVoiceProcessing] = useState(false);
  const scrollRef = useRef(null);
  const audioRef = useRef(null);
  const revealedStepRef = useRef(-1);
  const lastSpokenLineRef = useRef("");
  const recorderRef = useRef(null);
  const recorderChunksRef = useRef([]);
  const recorderStreamRef = useRef(null);

  const steps = STEPS(answers);
  const step = steps[stepIdx];

  useEffect(() => {
    if (user && !mentorId) navigate("/mentor", { replace: true });
  }, [user, mentorId, navigate]);

  useEffect(() => {
    if (user) {
      const fn = resolveFirstName(user, "");
      setAnswers((a) => ({ ...a, firstName: fn }));
    }
  }, [user]);

  useEffect(() => {
    if (!audioUnlocked) return; // wait for first-tap unlock
    if (stepIdx === 0 && messages.length === 0 && answers.firstName !== undefined && revealedStepRef.current !== 0) {
      revealedStepRef.current = 0;
      reveal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers.firstName, audioUnlocked]);

  useEffect(() => {
    if (!audioUnlocked) return;
    if (stepIdx > 0 && revealedStepRef.current !== stepIdx) {
      revealedStepRef.current = stepIdx;
      reveal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIdx, audioUnlocked]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, isMentorTyping]);

  const speak = async (text) => {
    if (!voiceOn || !audioUnlocked) return;
    lastSpokenLineRef.current = text;
    try {
      const token = localStorage.getItem("ascend_session_token");
      const resp = await fetch(`${API}/voice/tts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ text }),
      });
      if (!resp.ok) return;
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      if (audioRef.current) {
        audioRef.current.src = url;
        setCoachSpeaking(true);
        audioRef.current.onended = () => {
          URL.revokeObjectURL(url);
          setCoachSpeaking(false);
        };
        audioRef.current.onpause = () => setCoachSpeaking(false);
        audioRef.current.onerror = () => {
          URL.revokeObjectURL(url);
          setCoachSpeaking(false);
        };
        audioRef.current.play().catch(() => setCoachSpeaking(false));
      }
    } catch (e) {
      setCoachSpeaking(false);
    }
  };

  const repeatLastQuestion = () => {
    const currentStep = STEPS(answers)[stepIdx];
    if (!currentStep) return;
    const lines = typeof currentStep.bubbles === "function" ? currentStep.bubbles(answers) : currentStep.bubbles;
    // Speak the question line (last one is usually the actual question)
    const question = lines[lines.length - 1];
    if (question) speak(question);
  };

  // Tap-to-begin gate: many browsers (esp iOS) block any audio until a user
  // gesture. We use the first tap to "unlock" audio playback for the whole
  // session, then immediately start the conversation with voice on.
  const unlockAudioAndBegin = async () => {
    try {
      // Play and immediately pause an empty audio element under the same gesture
      // — this primes iOS Safari to allow programmatic .play() later.
      if (audioRef.current) {
        audioRef.current.muted = true;
        await audioRef.current.play().catch(() => {});
        audioRef.current.pause();
        audioRef.current.muted = false;
      }
    } catch (e) {}
    setAudioUnlocked(true);
    track("onboarding_audio_unlocked");
  };

  const reveal = async () => {
    const currentStep = STEPS(answers)[stepIdx];
    if (!currentStep) return;
    const lines = typeof currentStep.bubbles === "function" ? currentStep.bubbles(answers) : currentStep.bubbles;
    // Text-mode (voice off): snap-render all bubbles immediately. This matches
    // the "read instead" intent — no slow typing animations, no audio waits.
    if (!voiceOn) {
      setIsMentorTyping(false);
      setMessages((m) => [...m, ...lines.map((text) => ({ role: "assistant", text }))]);
      setRevealedBubbles(lines.length);
      return;
    }
    for (let i = 0; i < lines.length; i++) {
      setIsMentorTyping(true);
      await new Promise((r) => setTimeout(r, 550 + lines[i].length * 10));
      setIsMentorTyping(false);
      setMessages((m) => [...m, { role: "assistant", text: lines[i] }]);
      speak(lines[i]);
      await new Promise((r) => setTimeout(r, 200));
    }
    setRevealedBubbles(lines.length);
  };

  // ----- Voice answer flow -----
  // User taps the mic, speaks their answer, taps stop. We transcribe, then either
  // (a) match the transcript to the closest chip option (so taps and voice produce
  // the same canonical answer), or (b) submit as free text on type-mode questions.
  const startVoiceAnswer = async () => {
    if (recordingAnswer || voiceProcessing) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recorderStreamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4")
        ? "audio/mp4"
        : "";
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderChunksRef.current = [];
      mr.ondataavailable = (e) => e.data && e.data.size && recorderChunksRef.current.push(e.data);
      mr.onstop = handleVoiceAnswerStop;
      mr.start(100);
      recorderRef.current = mr;
      setRecordingAnswer(true);
    } catch (e) {
      toast.error("Mic permission denied. Tap a bubble to answer instead.");
    }
  };

  const stopVoiceAnswer = () => {
    if (!recorderRef.current) return;
    try {
      if (recorderRef.current.state === "recording") recorderRef.current.stop();
    } catch (e) {}
    try { recorderStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (e) {}
    setRecordingAnswer(false);
  };

  const handleVoiceAnswerStop = async () => {
    setVoiceProcessing(true);
    try {
      const blobType = recorderChunksRef.current[0]?.type || "audio/webm";
      const blob = new Blob(recorderChunksRef.current, { type: blobType });
      recorderChunksRef.current = [];
      const ext = blobType.includes("mp4") ? "m4a" : blobType.includes("ogg") ? "ogg" : "webm";
      const form = new FormData();
      form.append("audio", blob, `answer.${ext}`);
      const resp = await axios.post(`${API}/voice/stt`, form);
      const transcript = (resp.data.text || resp.data.transcript || "").trim();
      if (!transcript) {
        toast.error("Didn't catch that — try once more or tap a bubble.");
        return;
      }
      // Try to match to a chip
      const currentStep = STEPS(answers)[stepIdx];
      const chipsList = currentStep && typeof currentStep.chips === "function"
        ? currentStep.chips(answers)
        : currentStep?.chips || [];
      const matched = matchTranscriptToChip(transcript, chipsList);
      if (matched) {
        submitAnswer(matched.value, matched.label);
      } else if (currentStep?.typeable) {
        // free-text question — accept the transcript directly
        submitAnswer(transcript, transcript);
      } else {
        toast.error(
          `I heard "${transcript}" but didn't match an option. Tap one of the bubbles.`,
          { duration: 5000 }
        );
      }
    } catch (e) {
      toast.error("Voice transcription failed. Tap a bubble instead.");
    } finally {
      setVoiceProcessing(false);
    }
  };


  const submitAnswer = async (rawValue, displayLabel) => {
    const currentStep = STEPS(answers)[stepIdx];
    if (!currentStep) return;
    if (rawValue === "__type__") {
      setTypeMode(true);
      return;
    }
    const text = displayLabel || String(rawValue);
    setMessages((m) => [...m, { role: "user", text }]);
    track("onboarding_step_completed", { step: currentStep.id, value: String(rawValue).slice(0, 40) });

    const next = { ...answers, ...currentStep.save(rawValue) };
    setAnswers(next);
    setTypeMode(false);
    setTextValue("");

    const nextIdx = stepIdx + 1;
    if (nextIdx >= STEPS(next).length) {
      await finalize(next);
    } else {
      setTimeout(() => setStepIdx(nextIdx), 300);
    }
  };

  const finalize = async (final) => {
    setSubmitting(true);
    const mentor = getMentor(mentorId);
    const payload = buildOnboardingPayload(
      { ...final, coach_gender: mentor.voice.gender, coach_accent: mentor.voice.accent },
      mentorId
    );
    try {
      await axios.post(`${API}/onboarding`, payload);
      axios.post(`${API}/roadmap/generate`, {}).catch(() => {});
      track("onboarding_completed", { mentor_id: mentorId, focus: final.focus_area, path: final.path });
      celebrate("big");
      if (checkAuth) await checkAuth();
      const isElite = final.path === "elite";
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: isElite
            ? `Understood. Elite protocol activated. Routing you to your tier options — the Titan is your default strategist.`
            : `Okay ${final.firstName || "friend"} — I have what I need. Let me build your first roadmap. Give me a moment.`,
        },
      ]);
      setTimeout(() => navigate(isElite ? "/pricing?tier=elite" : "/dashboard"), 2500);
    } catch (e) {
      toast.error("Something went wrong saving. Try again.");
      setSubmitting(false);
    }
  };

  const chips = step && typeof step.chips === "function" ? step.chips(answers) : step?.chips;
  const showInput = !!step && typeMode;
  const mentorState = isMentorTyping ? "speaking" : "idle";

  return (
    <div
      data-testid="conversational-onboarding"
      className="min-h-screen flex flex-col"
      style={{ background: "radial-gradient(circle at 30% 0%, #faf6ec 0%, #f7f3ea 60%)" }}
    >
      {/* First-tap audio unlock — required for iOS / mobile browsers to play TTS */}
      {!audioUnlocked && (
        <div
          data-testid="onboarding-audio-unlock"
          className="fixed inset-0 z-50 bg-[#f7f3ea]/95 backdrop-blur-sm flex items-center justify-center px-6"
        >
          <div className="max-w-sm w-full bg-white border border-[#eadfc9] rounded-3xl p-8 shadow-[0_30px_70px_-20px_rgba(15,23,36,0.25)] text-center">
            <div className="flex justify-center mb-4">
              <Avatar id={mentorId || "human_maya"} size={88} state="speaking" />
            </div>
            <p className="font-mono-eyebrow text-[10px] text-[#486a8f] mb-3">YOUR AI COACH</p>
            <h2 className="font-display text-[26px] sm:text-[30px] leading-tight text-[#0f1724]">
              I'll guide you through this — out loud.
            </h2>
            <p className="font-body text-[14px] text-[#4b5563] mt-4 leading-relaxed">
              I'll ask you a few questions and listen to what matters most. You can answer by tapping a bubble or speaking back.
            </p>
            <Button
              data-testid="onboarding-begin-btn"
              onClick={unlockAudioAndBegin}
              className="mt-6 w-full h-12 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white font-medium text-[15px] btn-lift"
            >
              <Sparkles size={15} className="mr-2" /> Tap to begin
            </Button>
            <button
              onClick={() => { setVoiceOn(false); setAudioUnlocked(true); }}
              data-testid="onboarding-skip-voice-btn"
              className="font-body text-[12px] text-[#6b7280] underline mt-4 hover:text-[#486a8f]"
            >
              Or read instead (no audio)
            </button>
          </div>
        </div>
      )}
      <header className="flex items-center justify-between px-6 pt-6 max-w-3xl w-full mx-auto">
        <div className="flex items-center gap-1">
          <span className="font-display text-[20px] font-semibold text-[#0f1724]">ascend</span>
          <span className="font-display text-[20px] font-semibold text-[#486a8f]">higher</span>
        </div>
        <button
          data-testid="onboarding-voice-toggle"
          onClick={() => setVoiceOn((v) => !v)}
          className="inline-flex items-center gap-2 h-9 px-3 rounded-full bg-white border border-[#eadfc9] text-[12px] text-[#2b3442] hover:bg-[#faf6ec]"
        >
          {voiceOn ? <Volume2 size={13} /> : <VolumeX size={13} />}
          {voiceOn ? "Voice on" : "Voice off"}
        </button>
      </header>

      <div className="flex items-center justify-center pt-6 pb-2 relative">
        <Avatar id={mentorId || "human_maya"} size={120} state={coachSpeaking ? "speaking" : mentorState} />
        {coachSpeaking && (
          <div className="absolute -bottom-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#0f1724] text-white shadow-md">
            <span className="w-1.5 h-1.5 rounded-full bg-[#3e6b52] pulse-dot" />
            <span className="font-mono-eyebrow text-[10px] tracking-wide">SPEAKING…</span>
          </div>
        )}
      </div>

      <div className="px-5 max-w-2xl w-full mx-auto -mt-1 mb-1">
        <TrustLine className="text-center justify-center" />
      </div>

      <main ref={scrollRef} className="flex-1 overflow-y-auto px-5 max-w-2xl w-full mx-auto pb-4">
        <div className="space-y-3 py-4">
          {messages.map((m, i) =>
            m.role === "assistant" ? (
              <div key={i} className="max-w-[88%] mr-auto">
                <div className="rounded-2xl rounded-tl-md bg-white border border-[#eadfc9] px-4 py-3 shadow-sm">
                  <p className="font-body text-[15px] text-[#0f1724] leading-relaxed">{m.text}</p>
                </div>
              </div>
            ) : (
              <div key={i} className="max-w-[80%] ml-auto">
                <div className="rounded-2xl rounded-tr-md bg-[#486a8f] text-white px-4 py-3">
                  <p className="font-body text-[14px] leading-relaxed">{m.text}</p>
                </div>
              </div>
            )
          )}
          {isMentorTyping && (
            <div className="max-w-[60%] mr-auto">
              <div className="rounded-2xl rounded-tl-md bg-white border border-[#eadfc9] px-4 py-3 shadow-sm inline-flex items-center">
                <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
              </div>
            </div>
          )}
        </div>
      </main>

      {!submitting && step && revealedBubbles > 0 && !isMentorTyping && (
        <div className="px-5 pb-7 max-w-2xl w-full mx-auto">
          {/* Voice-answer + Repeat row */}
          {!showInput && (
            <div className="flex items-center justify-between mb-3 px-1">
              <button
                onClick={repeatLastQuestion}
                disabled={coachSpeaking || !voiceOn}
                data-testid="onboarding-repeat-btn"
                className="inline-flex items-center gap-1.5 font-body text-[12px] text-[#6b7280] hover:text-[#486a8f] disabled:opacity-40"
                type="button"
              >
                <RotateCcw size={12} /> Repeat
              </button>
              <button
                onClick={recordingAnswer ? stopVoiceAnswer : startVoiceAnswer}
                disabled={voiceProcessing || coachSpeaking}
                data-testid="onboarding-voice-answer-btn"
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-body text-[12px] font-medium transition-colors ${
                  recordingAnswer
                    ? "bg-[#c0392b] text-white animate-pulse"
                    : voiceProcessing
                    ? "bg-[#e7edf5] text-[#486a8f]"
                    : "bg-[#0f1724] text-white hover:bg-[#1c2944]"
                } disabled:opacity-50`}
                type="button"
              >
                {recordingAnswer ? (
                  <><Square size={11} /> Tap when done</>
                ) : voiceProcessing ? (
                  <>Transcribing…</>
                ) : (
                  <><Mic size={11} /> Speak my answer</>
                )}
              </button>
            </div>
          )}
          {!showInput && chips && chips.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-end">
              {chips.map((c) => {
                const eliteStyle = answers.path === "elite";
                return (
                  <button
                    key={c.value}
                    data-testid={`onboarding-chip-${c.value}`}
                    onClick={() => submitAnswer(c.value, c.label)}
                    className={
                      eliteStyle
                        ? "px-4 h-auto py-3 rounded-full elite-chip text-[13px] font-medium transition-colors flex flex-col items-start text-left"
                        : "px-4 h-auto py-3 rounded-2xl bg-white border-2 border-[#c8d6dd] text-[13px] font-medium text-[#0f1724] hover:border-[#486a8f] hover:bg-[#e7edf5] transition-colors flex flex-col items-start text-left"
                    }
                  >
                    <span>{c.label}</span>
                    {c.sub && (
                      <span className={`font-body text-[11px] mt-0.5 ${eliteStyle ? "text-[#c9a14a]/80" : "text-[#6b7280]"}`}>
                        {c.sub}
                      </span>
                    )}
                  </button>
                );
              })}
              {step.typeable && (
                <button
                  data-testid="onboarding-chip-type-own"
                  onClick={() => setTypeMode(true)}
                  className="px-4 h-11 rounded-full bg-[#faf6ec] border-2 border-dashed border-[#c8b88a] text-[13px] font-medium text-[#7a5a30] hover:bg-[#f2ecdc]"
                >
                  Type my own…
                </button>
              )}
            </div>
          )}
          {showInput && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!textValue.trim()) return;
                submitAnswer(textValue.trim(), textValue.trim());
              }}
              className="flex items-center gap-2"
            >
              <input
                data-testid="onboarding-text-input"
                autoFocus
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                placeholder={step.placeholder || "Type here…"}
                maxLength={200}
                className="flex-1 h-12 px-4 rounded-full bg-white border-2 border-[#c8d6dd] focus:border-[#486a8f] outline-none font-body text-[14px]"
              />
              <Button
                data-testid="onboarding-send-btn"
                type="submit"
                disabled={!textValue.trim()}
                className="w-12 h-12 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white p-0"
              >
                <Send size={16} />
              </Button>
            </form>
          )}
          <div className="flex items-center justify-center gap-1.5 mt-4">
            {STEPS(answers).map((_, i) => (
              <span
                key={i}
                className={`h-1 rounded-full transition-all ${
                  i < stepIdx ? "w-6 bg-[#3e6b52]" : i === stepIdx ? "w-8 bg-[#0f1724]" : "w-3 bg-[#eadfc9]"
                }`}
              />
            ))}
          </div>
        </div>
      )}

      {submitting && (
        <div className="px-5 pb-10 max-w-2xl w-full mx-auto text-center">
          <p className="font-body text-[14px] text-[#486a8f] mt-2 inline-flex items-center gap-2">
            <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
            Building your roadmap…
          </p>
        </div>
      )}

      <audio ref={audioRef} hidden />
    </div>
  );
}

/**
 * Fuzzy-match a transcribed answer to one of the chip options.
 * Returns the chip object {value, label} or null if no good match found.
 * Heuristic:
 *   1. Exact (case-insensitive) match on label or value → highest priority
 *   2. The chip whose label has the most word overlap with the transcript
 *   3. Fall back to substring containment in either direction
 */
function matchTranscriptToChip(transcript, chips) {
  if (!transcript || !chips?.length) return null;
  const t = transcript.toLowerCase().trim();

  // 1. Exact match
  for (const c of chips) {
    if (c.label.toLowerCase() === t || String(c.value).toLowerCase() === t) {
      return c;
    }
  }

  // 2. Word-overlap scoring
  const stopWords = new Set([
    "the", "a", "an", "is", "are", "i", "im", "i'm", "my", "me", "to", "of",
    "and", "or", "but", "want", "need", "feel", "feeling", "with", "for",
    "on", "in", "at", "be", "do", "have", "has", "it", "its", "this", "that",
    "really", "kind", "sort", "ok", "yeah", "yes", "no", "just",
  ]);
  const tokenize = (s) =>
    s.toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter((w) => w && !stopWords.has(w));
  const tWords = new Set(tokenize(t));

  let best = null;
  let bestScore = 0;
  for (const c of chips) {
    const cWords = tokenize(`${c.label} ${c.sub || ""} ${c.value}`);
    let score = 0;
    for (const w of cWords) if (tWords.has(w)) score += 1;
    // bonus for substring containment in either direction
    const lbl = c.label.toLowerCase();
    if (t.includes(lbl) || lbl.includes(t)) score += 2;
    if (score > bestScore) {
      bestScore = score;
      best = c;
    }
  }

  return bestScore >= 1 ? best : null;
}
